import type { ItemState, MonthlyPlan, PlanBriefRow, PlanPostRow, Production } from '@/services/brandKnowledgeApi';
import type { CalendarItem } from '@/components/growth-desk/data/schedule';
import type { BarTone, DeskStatus } from '../ui';
import { DESK } from '../links';

/** Per-campaign numbers from the existing analytics summary (joined on the conversation id). */
export interface CampaignNumbers {
  spend: number | null; results: number | null; costPerResult: number | null; currency: string | null;
  resultType: string | null; delivering: boolean | null; status: string | null;
}

/** One plan campaign as the desk shows it: status word, bar tone, and where it opens. */
export interface DeskCampaign {
  row: PlanBriefRow;
  production: Production | null;
  scheduled: CalendarItem | null;
  numbers: CampaignNumbers | null;
  status: DeskStatus;
  statusDetail?: string;
  tone: BarTone;
  barLabel: string;
  to: string;
  start: Date;
  end: Date;
}

export const CHANNEL_NAME: Record<string, string> = {
  meta: 'Meta ads', facebook: 'Meta ads', instagram: 'Meta ads', google: 'Google Ads', google_ads: 'Google Ads',
  pinterest: 'Pinterest', x: 'X', twitter: 'X', linkedin: 'LinkedIn', tiktok: 'TikTok',
  instagram_organic: 'Instagram', facebook_organic: 'Facebook page', linkedin_organic: 'LinkedIn', pinterest_organic: 'Pinterest', youtube_organic: 'YouTube',
};
export const channelName = (c?: string | null) => (c ? CHANNEL_NAME[c.toLowerCase()] || c : '');

/** Plan goal → owner words ("Sign-ups", "Leads"). */
export const GOAL_NAME: Record<string, string> = {
  leads: 'Leads', lead: 'Leads', sales: 'Sales', sign_ups: 'Sign-ups', signups: 'Sign-ups', sign_up: 'Sign-ups',
  bookings: 'Bookings', calls: 'Calls', messages: 'WhatsApp messages', whatsapp: 'WhatsApp messages',
  awareness: 'Awareness', store_visits: 'Store visits', app_installs: 'App installs', traffic: 'Website visits',
};
export const goalName = (g?: string | null) => (g ? GOAL_NAME[g.toLowerCase()] || g.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()) : '');

export const day = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00`);
export const shortDate = (d: Date | string) => (typeof d === 'string' ? day(d) : d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
export const shortDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} ${d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}`;
};
export const monthLabel = (m: string, withYear = true) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', withYear ? { month: 'long', year: 'numeric' } : { month: 'long' });
export const thisMonth = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; };
export const shiftMonth = (m: string, by: number) => {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 1 + by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
/** What a month allows, as the server decides it (plan/pins.py HORIZON = 3): past, plan (this
 *  month and next), ahead (posts and pins), later (nothing yet). */
export const monthMode = (m: string): 'past' | 'plan' | 'ahead' | 'later' => {
  const [y, mo] = m.split('-').map(Number);
  const n = new Date();
  const diff = (y - n.getFullYear()) * 12 + (mo - 1 - n.getMonth());
  return diff < 0 ? 'past' : diff <= 1 ? 'plan' : diff <= 3 ? 'ahead' : 'later';
};
export const MONTH_MODE_TAG = { past: 'Past', plan: 'Plan', ahead: 'Posts & pins', later: 'Later' } as const;

export const monthBounds = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  return { start: new Date(y, mo - 1, 1), end: new Date(y, mo, 0) };
};

export const money = (n: number | null | undefined, currency = 'INR') => {
  if (n == null || !Number.isFinite(n)) return '—';
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString('en-IN')}`; }
};

const LIVE_SCHEDULE = new Set(['published', 'partially_published']);
const FAILED_SCHEDULE = new Set(['failed', 'partially_failed']);

/** The scheduled publish that belongs to a campaign (latest that isn't cancelled). */
export const scheduleFor = (conversationId: string | null | undefined, items: CalendarItem[]) =>
  conversationId ? items.filter(i => i.conversation_id === conversationId && i.status !== 'cancelled')
    .sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at))[0] ?? null : null;

/**
 * A plan campaign's state, most decisive first: what is live or scheduled, then production,
 * then the brief itself. Status always comes with words for the bar.
 */
export function describeCampaign(
  row: PlanBriefRow, production: Production | null, scheduled: CalendarItem | null, numbers: CampaignNumbers | null,
  currency: string, month: string, today = new Date(), state?: ItemState,
): DeskCampaign {
  const b = row.brief;
  const start = day(b.start_date), end = day(b.end_date);
  const cid = production?.campaign_id || row.campaign_id;
  const base = { row, production, scheduled, numbers, start, end };
  const resultsWord = numbers?.results != null && numbers.results > 0
    ? `${numbers.results} ${goalName(b.objective).toLowerCase() || 'results'}${numbers.costPerResult ? ` · ${money(numbers.costPerResult, numbers.currency || currency)} each` : ''}`
    : null;

  if (row.status === 'skipped') return { ...base, status: 'skipped', tone: 'skipped', barLabel: 'Skipped this month', to: DESK.plan(month) };
  if (b.continues && row.status !== 'suggested') {
    // keeps a running campaign going: the owner confirms once, nothing is made (plan/continuing.py)
    const what = b.continue_of?.name ? `“${b.continue_of.name}”` : 'the campaign';
    switch (state) {
      case 'to_confirm': return { ...base, status: 'needs_you', statusDetail: 'confirm', tone: 'needs', barLabel: `Keeps ${what} running · confirm it`, to: DESK.plan(month) };
      case 'scheduled': return { ...base, status: 'scheduled', statusDetail: `from ${shortDate(b.start_date)}`, tone: 'scheduled', barLabel: `Confirmed · keeps running from ${shortDate(b.start_date)}`, to: DESK.plan(month) };
      case 'published': return { ...base, status: 'live', tone: 'live', barLabel: resultsWord ? `Live · ${resultsWord}` : 'Live · kept running', to: cid ? DESK.campaignResults(cid) : DESK.plan(month) };
      case 'failed': return { ...base, status: 'needs_you', statusDetail: 'not changed', tone: 'needs', barLabel: `Couldn't keep ${what} running · try again`, to: DESK.plan(month) };
      case 'missed': return { ...base, status: 'needs_you', statusDetail: 'missed', tone: 'needs', barLabel: 'Not confirmed before its start · confirm now or skip', to: DESK.plan(month) };
      default: break;
    }
  }
  if (scheduled && LIVE_SCHEDULE.has(scheduled.status) && cid) {
    const ended = end < new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return ended
      ? { ...base, status: 'ended', tone: 'ended', barLabel: resultsWord ? `Ended · ${resultsWord}` : 'Ended', to: DESK.campaignResults(cid) }
      : { ...base, status: 'live', tone: 'live', barLabel: resultsWord ? `Live · ${resultsWord}` : 'Live', to: DESK.campaignResults(cid) };
  }
  if (scheduled && FAILED_SCHEDULE.has(scheduled.status) && cid)
    return { ...base, status: 'needs_you', statusDetail: 'publishing failed', tone: 'needs', barLabel: 'Publishing failed · open the Launch step', to: DESK.campaign(cid) };
  if (scheduled && (scheduled.status === 'scheduled' || scheduled.status === 'publishing') && cid) {
    const at = shortDateTime(scheduled.scheduled_at);
    return { ...base, status: 'scheduled', statusDetail: at, tone: 'scheduled', barLabel: `Scheduled · ${at}`, to: DESK.campaign(cid) };
  }
  if (state === 'failed' && cid)          // a launch now that failed leaves no scheduled publish, only this
    return { ...base, status: 'needs_you', statusDetail: 'publishing failed', tone: 'needs', barLabel: 'Publishing failed · open the Launch step', to: DESK.campaign(cid) };
  if (state === 'missed') {
    const launchable = production?.status === 'ready' && end >= new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return { ...base, status: 'needs_you', statusDetail: 'missed', tone: 'needs',
      barLabel: launchable ? 'Missed its start · launch now, move or skip' : 'Missed its start · move or skip',
      to: launchable && production ? (production.approved_at && cid ? DESK.campaign(cid) : DESK.review(production.id)) : DESK.plan(month) };
  }
  if (production) {
    const p = production;
    switch (p.status) {
      case 'needs_input':
        return { ...base, status: 'needs_you', statusDetail: 'a question', tone: 'needs', barLabel: 'Needs you · a question from production', to: DESK.production(month, p.id) };
      case 'failed':
        return { ...base, status: 'stopped', tone: 'needs', barLabel: 'Stopped · retry continues where it stopped', to: DESK.production(month, p.id) };
      case 'running':
        return { ...base, status: 'in_production', tone: 'working', barLabel: p.total ? `Making · ad ${Math.min(p.done + 1, p.total)} of ${p.total}` : `Making · ${p.step_label || 'setting up'}`, to: DESK.production(month, p.id) };
      case 'queued':
        return { ...base, status: 'waiting', tone: 'working', barLabel: 'Waiting · next in line', to: DESK.production(month, p.id) };
      case 'paused':
        return { ...base, status: 'paused', tone: 'planned', barLabel: 'Paused · nothing more is spent', to: DESK.production(month, p.id) };
      case 'ready':
        return p.approved_at
          ? { ...base, status: 'approved', tone: 'approved', barLabel: 'Approved · confirm accounts in the Launch step', to: cid ? DESK.campaign(cid) : DESK.review(p.id) }
          : { ...base, status: 'ready', tone: 'review', barLabel: `Ready for your review · ${p.ads.length} ad${p.ads.length === 1 ? '' : 's'}`, to: DESK.review(p.id) };
      default: break;   // cancelled → treat as not made
    }
  }
  if (row.status === 'stale') return { ...base, status: 'changed', tone: 'planned', barLabel: 'Changed since its campaign', to: DESK.plan(month) };
  if (row.status === 'approved' || row.status === 'draft')
    return { ...base, status: 'planned', tone: 'planned', barLabel: 'Approved · not made yet', to: DESK.prepare(month) };
  return { ...base, status: 'suggested', tone: 'planned', barLabel: 'Suggested · waiting for your OK', to: DESK.plan(month) };
}

/** Joins a plan's briefs with productions, scheduled publishes and analytics numbers. */
export function planCampaigns(plan: MonthlyPlan, productions: Production[], scheduled: CalendarItem[], numbers: Record<string, CampaignNumbers>): DeskCampaign[] {
  const currency = plan.context.currency || 'INR';
  const live = productions.filter(p => p.status !== 'cancelled');
  return plan.briefs.map(row => {
    const prod = live.find(p => p.brief_id === row.id) || null;
    const cid = prod?.campaign_id || row.campaign_id;
    return describeCampaign(row, prod, scheduleFor(cid, scheduled), cid ? numbers[cid] ?? null : null, currency, plan.month,
      new Date(), plan.item_states?.[row.id]);
  }).sort((a, b) => a.start.getTime() - b.start.getTime());
}

/** One plan post as the desk shows it: status word, tone, and where it opens. */
export interface DeskPost {
  row: PlanPostRow;
  production: Production | null;
  state: ItemState;
  word: string;
  tone: BarTone;
  urgent: boolean;
  to: string;
  date: Date;
}

/** A plan post's state in owner words; the daily sweep's state decides, production adds detail. */
export function describePost(row: PlanPostRow, production: Production | null, state: ItemState | undefined, month: string): DeskPost {
  const p = production;
  const st: ItemState = state || (row.status === 'skipped' ? 'skipped' : row.status === 'suggested' ? 'suggested' : p ? (p.status === 'ready' ? 'ready' : 'making') : 'not_started');
  const base = { row, production: p, state: st, date: day(row.post.date) };
  const review = p ? DESK.postReview(p.id) : DESK.plan(month);
  switch (st) {
    case 'published': return { ...base, word: 'Posted', tone: 'live', urgent: false, to: review };
    case 'scheduled': return { ...base, word: p?.schedule?.at ? `Scheduled · ${shortDateTime(p.schedule.at)}` : 'Scheduled', tone: 'scheduled', urgent: false, to: review };
    case 'failed': return { ...base, word: 'Posting failed · open it to try again', tone: 'needs', urgent: true, to: review };
    case 'missed': return { ...base, word: 'Missed its date · move or skip', tone: 'needs', urgent: true, to: DESK.plan(month) };
    case 'ready': return { ...base, word: 'Ready for your review', tone: 'review', urgent: false, to: review };
    case 'approved': return { ...base, word: 'Approved', tone: 'approved', urgent: false, to: review };
    case 'making': {
      if (p?.status === 'needs_input') return { ...base, word: 'Needs you · a question from production', tone: 'needs', urgent: true, to: DESK.production(month, p.id) };
      if (p?.status === 'failed') return { ...base, word: 'Stopped · retry continues where it stopped', tone: 'needs', urgent: true, to: DESK.production(month, p.id) };
      if (p?.status === 'paused') return { ...base, word: 'Paused', tone: 'planned', urgent: false, to: DESK.production(month, p.id) };
      return { ...base, word: p?.status === 'queued' ? 'Waiting · next in line' : 'Making', tone: 'working', urgent: false, to: p ? DESK.production(month, p.id) : DESK.plan(month) };
    }
    case 'skipped': return { ...base, word: 'Skipped', tone: 'skipped', urgent: false, to: DESK.plan(month) };
    case 'suggested': return { ...base, word: 'Waiting for your OK', tone: 'planned', urgent: false, to: DESK.plan(month) };
    default: return { ...base, word: 'Approved · not made yet', tone: 'planned', urgent: false, to: DESK.prepare(month) };
  }
}

/** A post's dot: solid once it is scheduled or out, red when it needs you, outlined while planned. */
export const postDot = (x: DeskPost) =>
  x.state === 'published' ? 'bg-ok' : x.state === 'scheduled' ? 'bg-navy-ink' : x.urgent ? 'bg-bad'
    : x.state === 'ready' ? 'bg-brand' : 'shadow-[inset_0_0_0_1.5px_rgb(var(--c-navy-ink))]';

export function planPosts(plan: MonthlyPlan, productions: Production[]): DeskPost[] {
  const live = productions.filter(p => p.status !== 'cancelled');
  return (plan.posts || []).map(row => describePost(row, live.find(p => p.post_id === row.id) || null, plan.item_states?.[row.id], plan.month))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}
